// The desktop mini-app (Brief 4 / P6): the floating per-session CHAT CHANNEL.
//
// A distinct product, NOT a shrunk Dashboard: each agent_session is a channel
// (mini-Slack). The left rail is the channel list (needs-me first, the one
// reserved attention signal + the typed reason in words); the open channel keeps
// the CONVERSATION and the live ACTIVITY as two separate regions (never a merged
// log), with the one-thumb optimistic brake. Token-pure (var(--waytide-sys-*));
// reuses the shared transport verbatim (no mock data). Reviewable at /channels;
// the Tauri floating-window shell is the net-new wrapper (P6 follow-on).
//
// PLATFORM BOUNDARY: this is SHARED UI in @waytide/ui, so it
// owns NO Tauri. The platform-specific bits inject as PROPS at the app edge:
//   - `machineId`   — scope the rail to THIS machine (desktop) or show all (web,
//                     null); the same machine-scoping the runner stamps.
//   - `bridgeStatus`— the desktop bridge's connect lifecycle, for the rail copy;
//                     the web passes "idle" (inert — it never connects a bridge).
//   - `connectSlot` — the first-run Connect flow to render when nothing is
//                     connected yet (desktop passes <ConnectFlow/>; web passes
//                     null — a browser never shows it, and with machineId null
//                     the needs-connect branch can't trigger anyway).

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import type { SessionPresence } from "@waytide/core";
import { sessionLabel, tracePresentation } from "@waytide/core";
import { usePresenceContext } from "../hooks/PresenceContext";
import {
  cellTone,
  orderSessions,
  reasonLabel,
  statusLabel,
} from "../model/presenceModel";
import { operatorForKind } from "../model/operators";
import { streamHealthCue } from "../model/streamHealth";
import { useChannel } from "../hooks/useChannel";
import { useReadWritePosture } from "../hooks/useReadWritePosture";
import { useSchematic } from "../hooks/useSchematic";
import { usePersistentDraft } from "../hooks/usePersistentDraft";
import { useProjectBrain } from "../hooks/useProjectBrain";
import { useIncidents } from "../hooks/useIncidents";
import { deriveSessionStatus } from "@waytide/core";
import { StreamHealthBadge } from "./StreamHealthBadge";
import { IncidentNotices } from "./IncidentNotices";
import { ReadOnlyIndicator } from "./ReadOnlyIndicator";
import { ProjectSchematic } from "./ProjectSchematic";
import { SessionStatusPanel } from "./SessionStatusPanel";
import { WikiPanel } from "./WikiPanel";
import { OperatorsDrawer } from "./OperatorsDrawer";
import "../styles/channels.css";

/** The desktop bridge's connect lifecycle, surfaced for the rail copy. The web
 * passes "idle" (it never runs a bridge). Mirrors the Tauri shell's status wire. */
export type BridgeStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "stopped"
  | "errored";

export interface ChannelsPageProps {
  /** This machine's id (desktop) to scope the rail, or null (web → show all). */
  machineId?: string | null;
  /** The desktop bridge status for the rail copy; "idle" on web. */
  bridgeStatus?: BridgeStatus;
  /** The first-run Connect flow to show when nothing is connected (desktop);
   * null on web. Rendered only on the desktop's needs-connect branch. */
  connectSlot?: ReactNode;
}

export function ChannelsPage({
  machineId = null,
  bridgeStatus = "idle",
  connectSlot = null,
}: ChannelsPageProps) {
  const { sessions } = usePresenceContext();
  const channels = useMemo(() => {
    const ordered = orderSessions(sessions);
    // The desktop scopes to THIS machine; the web/browser (machineId == null)
    // shows all. Unattributed sessions (no machine_id yet) show on both, so the
    // rollout never hides a live session before the runner has stamped it.
    if (!machineId) return ordered;
    return ordered.filter((s) => !s.machine_id || s.machine_id === machineId);
  }, [sessions, machineId]);
  const [selected, setSelected] = useState<number | null>(null);
  const [searchParams] = useSearchParams();

  // A deep link (waytide://thread/<id> → /channels?thread=<id>) selects that
  // channel, even while the surface is already open.
  useEffect(() => {
    const t = Number(searchParams.get("thread"));
    if (Number.isFinite(t) && t > 0) setSelected(t);
  }, [searchParams]);

  // Otherwise default to the channel that most needs you (the ordered first),
  // so the mini-app opens on the thing to handle — not an empty pane.
  useEffect(() => {
    if (selected == null && channels.length > 0) setSelected(channels[0].thread_id);
  }, [channels, selected]);

  const active = channels.find((c) => c.thread_id === selected) ?? null;

  // Desktop, nothing connected yet → the first-run Connect flow (passed as the
  // slot). The browser (machineId == null) never reaches this; once a session is
  // connected, presence populates the rail and this clears. We only show the
  // slot when the app actually provided one — never a blank surface.
  const needsConnect =
    machineId != null &&
    connectSlot != null &&
    channels.length === 0 &&
    (bridgeStatus === "idle" || bridgeStatus === "stopped");
  if (needsConnect) return <>{connectSlot}</>;

  return (
    <div className="wt-channels">
      <aside className="wt-channels__rail" aria-label="Channels">
        <div className="wt-channels__rail-head">Channels</div>
        {channels.length === 0 ? (
          <p className="wt-channels__empty">
            {bridgeStatus === "connecting" || bridgeStatus === "connected"
              ? "Connecting… looking for your session."
              : "No active sessions yet."}
          </p>
        ) : (
          <ul className="wt-channels__list">
            {channels.map((s) => (
              <ChannelRow
                key={s.thread_id}
                session={s}
                active={s.thread_id === selected}
                onSelect={() => setSelected(s.thread_id)}
              />
            ))}
          </ul>
        )}
      </aside>
      <section className="wt-channels__open" aria-label="Channel">
        {active ? (
          // Key by session id so each channel is a FRESH instance: its per-session
          // read/write posture (the once-per-session hook-injection write, the
          // write count) resets cleanly when you switch channels (Brief 5 / pt9).
          <ChannelView key={active.thread_id} session={active} />
        ) : (
          <p className="wt-channels__empty">Select a channel.</p>
        )}
      </section>
    </div>
  );
}

function ChannelRow({
  session,
  active,
  onSelect,
}: {
  session: SessionPresence;
  active: boolean;
  onSelect: () => void;
}) {
  const tone = cellTone(session);
  const reason = reasonLabel(session.needs_input_reason);
  return (
    <li>
      <button
        type="button"
        className={`wt-channel-row wt-channel-row--${tone}${
          active ? " wt-channel-row--active" : ""
        }`}
        data-tone={tone}
        onClick={onSelect}
      >
        <span className="wt-channel-row__name" title={`Session #${session.thread_id}`}>
          {sessionLabel(session)}
        </span>
        <span className="wt-channel-row__status">
          <span className="wt-channel-row__signal" aria-hidden="true" />
          {statusLabel(session.status)}
        </span>
        {reason ? (
          <span className="wt-channel-row__reason">{reason}</span>
        ) : null}
      </button>
    </li>
  );
}

function ChannelView({ session }: { session: SessionPresence }) {
  const {
    messages,
    activity,
    health,
    lastEventAt,
    send,
    pending: outbox,
    retry,
    stop,
    resume,
  } = useChannel(session.thread_id);
  // Stream-honesty (Brief 5): only `live` reads as calm. When the feed isn't
  // live we dim the two live regions and the header shows a quiet cue.
  const streamCue = streamHealthCue(health, lastEventAt);
  // The project brain (Brief 5): the wiki Signal builds + its one open question.
  // Idle until the session has a project (Signal auto-creates one — no orphans).
  const brain = useProjectBrain(session.project_id ?? null);
  // The composer draft is persisted per thread (Brief 5 / point 15): a reload or
  // crash mid-typing restores the exact text; switching channels keeps each
  // channel's own draft.
  const { draft, setDraft, clearDraft } = usePersistentDraft(session.thread_id);
  // The project schematic (Brief 5 / pt7): the bound repo's shape, fetched once
  // (server-cached). The read/write indicator points a write's location at it.
  const schematic = useSchematic(session.thread_id);
  const repoFullName =
    schematic.kind === "ready" ? schematic.schematic.full_name : "";
  // The read/write posture (Brief 5 / pt7 + pt9): READ-ONLY at rest, a named
  // momentary WRITE on a confirmed fact (the channel attaching = the hook
  // injection; a steer that landed over presence). `attached` = the stream is
  // confirmed live — the honest "we have a real channel" signal both surfaces share.
  const posture = useReadWritePosture({
    threadId: session.thread_id,
    attached: health === "live",
    status: session.status,
    repoFullName,
  });
  // The technical status digest (Brief 5 / pt11) — the "catch me up technically"
  // view, derived (pure, in @waytide/core) from this channel's already-live data.
  // Facts only; no predictions. Steering asides aren't parsed in this shared view
  // (web parses them), so `openDecisions` is omitted — the digest still surfaces a
  // `needs_input` presence question, which this view has.
  const statusDigest = deriveSessionStatus({
    health,
    lastEventAt,
    session,
    activity,
  });
  // The PUSH recovery notices (Brief 5 / pt10 + pt20): a caught-and-handled
  // incident (the feed dropped + reconnected; a send that failed, retried, or got
  // parked) is surfaced PROACTIVELY at the top of the thread — complementing the
  // pull-view SessionStatusPanel. Fed by the SAME live health + outbox the regions
  // below already render, so it never invents a state. `failed` → `parked` (the
  // auto-retry gave up). FACTS only, classified in @waytide/core.
  const { notices, dismiss } = useIncidents({
    health,
    outbox: outbox.map((e) => ({
      key: e.key,
      attempts: e.attempts,
      parked: e.failed ?? false,
    })),
  });
  const [view, setView] = useState<"chat" | "wiki" | "map">("chat");
  // Optimistic steer echo: clears when the session's TRUE state lands over
  // presence (the session prop updates from live presence) — never asserts
  // stopped before the server confirms.
  const [pendingSteer, setPendingSteer] = useState<"stop" | "resume" | null>(null);
  const stopped = session.status === "paused" || session.status === "halted";
  useEffect(() => setPendingSteer(null), [session.status]);

  const pending = brain.pending;

  return (
    <div className="wt-channel">
      <header className="wt-channel__head">
        <span className="wt-channel__title" title={`Session #${session.thread_id}`}>
          {sessionLabel(session)}
        </span>
        <span className="wt-channel__state">{statusLabel(session.status)}</span>
        {/* The calm live-feed honesty cue (Brief 5): live = a faint heartbeat,
            not-live = a quiet "reconnecting…" / "stream lost" pill. */}
        <StreamHealthBadge health={health} lastEventAt={lastEventAt} />
        {/* Read-only transparency (Brief 5 / pt7 + pt9): the persistent proof that
            Waytide is READ-ONLY ~99% of the time; it names a genuine write
            (the hook injection / a steer that reached the agent) momentarily. */}
        <ReadOnlyIndicator resolved={posture} />
        <button
          type="button"
          className="wt-channel__view"
          onClick={() =>
            setView((v) => (v === "chat" ? "wiki" : v === "wiki" ? "map" : "chat"))
          }
          aria-label={`view: ${view}`}
        >
          {view === "chat" ? "Brain" : view === "wiki" ? "Map" : "Chat"}
        </button>
        {stopped ? (
          <button
            type="button"
            className="wt-channel__steer wt-channel__steer--resume"
            onClick={() => {
              setPendingSteer("resume");
              void resume();
            }}
          >
            {pendingSteer === "resume" ? "Resuming…" : "Resume"}
          </button>
        ) : (
          <button
            type="button"
            className="wt-channel__steer wt-channel__steer--stop"
            onClick={() => {
              setPendingSteer("stop");
              void stop();
            }}
          >
            {pendingSteer === "stop" ? "Stopping…" : "Stop"}
          </button>
        )}
      </header>

      {/* Main pane (chat regions OR the wiki) + the operators drawer, right. */}
      <div className="wt-channel__body">
        <div className="wt-channel__main">
          {/* The PUSH recovery notices (Brief 5 / pt10 + pt20): a thin transient
              stack at the TOP of the thread — calm + auto-dismissing for a handled
              recovery, the reserved amber only for a send the auto-retry gave up
              on. Stays legible (outside the dim wrapper) and renders nothing when
              there's nothing to say, so it never competes with the chat. */}
          <IncidentNotices notices={notices} onDismiss={dismiss} />
          {view === "map" ? (
            /* The map view pairs the project schematic (Brief 5 / pt7 — the bound
               repo's shape) with the technical status report (pt11 — the "catch
               me up technically" digest), so the structural + the factual sit
               together, away from the chat. The status panel is collapsed by
               default so it stays secondary. */
            <div className="wt-channel__map">
              <ProjectSchematic state={schematic} />
              <SessionStatusPanel digest={statusDigest} />
            </div>
          ) : view === "wiki" ? (
            <WikiPanel
              sections={brain.sections}
              questions={brain.questions}
              pendingSection={pending?.section ?? null}
            />
          ) : (
            /* Conversation and activity are TWO regions, never one merged log.
               Both dim (~60%) when the stream isn't live, so a stale feed is
               visibly stale rather than passing for calm-live (Brief 5). */
            <div
              className={`wt-channel__regions${
                streamCue.dim ? " wt-stream-dim" : ""
              }`}
            >
              <div className="wt-channel__region">
                <div className="wt-channel__region-head">Conversation</div>
                <ul className="wt-channel__messages">
                  {messages.map((m) => {
                    const op = operatorForKind(m.sender_kind);
                    return (
                      <li key={m.id} className="wt-channel__msg">
                        <span
                          className={`wt-channel__msg-who${op ? ` ${op.accentClass}` : ""}`}
                        >
                          {op
                            ? op.name
                            : m.sender_kind === "orchestrator"
                              ? m.sender_email
                              : m.sender_kind}
                        </span>
                        <span className="wt-channel__msg-body">{m.body}</span>
                      </li>
                    );
                  })}
                  {/* Pending sends (Brief 5 / point 15): persisted, retrying on
                      reconnect, shown so the user's words are never invisible. */}
                  {outbox.map((p) => (
                    <li
                      key={p.key}
                      className={`wt-channel__msg wt-channel__msg--pending${
                        p.failed ? " wt-channel__msg--failed" : ""
                      }`}
                      data-testid="pending-message"
                      data-pending="true"
                      data-failed={p.failed ? "true" : undefined}
                    >
                      <span className="wt-channel__msg-who">You</span>
                      <span className="wt-channel__msg-body">{p.body}</span>
                      <span className="wt-channel__msg-state">
                        {p.failed
                          ? `Couldn't send${p.lastError ? `: ${p.lastError}` : ""}`
                          : p.attempts > 0
                            ? "Reconnecting… will send"
                            : "Sending…"}
                      </span>
                      {p.failed ? (
                        <button
                          type="button"
                          className="wt-channel__retry"
                          data-testid="pending-retry"
                          onClick={() => retry(p.key)}
                        >
                          Retry
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="wt-channel__region wt-channel__region--activity">
                <div className="wt-channel__region-head">Activity</div>
                {activity.length === 0 ? (
                  /* Empty copy gated on health: only promise a live stream when
                     the feed is actually live; otherwise say it isn't. */
                  <p className="wt-channel__empty" data-testid="activity-empty">
                    {health === "live"
                      ? "Nothing yet. Activity streams here live."
                      : "Activity stream isn't connected right now."}
                  </p>
                ) : (
                  <ul className="wt-channel__activity">
                    {activity.map((a, i) => (
                      <li
                        key={`${a.id}-${a.created_at}-${i}`}
                        className="wt-channel__act"
                      >
                        <span className="wt-channel__act-kind">
                          {tracePresentation(a.kind).label}
                        </span>
                        <span className="wt-channel__act-summary">
                          {a.summary}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
        <OperatorsDrawer messages={messages} pending={pending != null} />
      </div>

      {/* The one reserved amber: Signal's open question, answered in the composer. */}
      {pending ? (
        <div className="wt-channel__needs" data-testid="needs-you">
          <span className="wt-channel__needs-who">Signal needs you</span>
          <span className="wt-channel__needs-q">{pending.question}</span>
        </div>
      ) : null}

      <form
        className="wt-channel__composer"
        onSubmit={(e) => {
          e.preventDefault();
          const body = draft.trim();
          if (body === "") return;
          // The outbox persists `body` the instant we hand it over, so clearing
          // the composer here can't lose it (point 15). Signal's open question
          // updates live over the `brain` SSE frame when the orchestrator folds
          // the answer in — no manual re-pull needed.
          void send(body);
          clearDraft();
        }}
      >
        <input
          className="wt-channel__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={pending ? "Answer Signal…" : "Message this session…"}
          aria-label={pending ? "Answer Signal" : "Message this session"}
        />
        <button type="submit" className="wt-channel__send" disabled={!draft.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}
